/* Copyright (c) 2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useParams } from "react-router-dom";
import { Box, Spacer, Text } from "@nordcloud/gnui";
import { FlexContainer, QueryPagination, showSuccess } from "~/components";
import { ContractCreate } from "~/components/Contract/ContractCreate";
import { ContractItem } from "~/components/Contract/ContractItem";
import { useShowError } from "~/components/Error/useShowError";
import { LoaderWrap } from "~/components/LoaderWrap/LoaderWrap";
import { Action } from "~/constants/actions";
import { useUserConsumer } from "~/context/User/User";
import { RouteParams } from "~/models";
import { Contract, SubmitContract } from "~/models/contracts";
import { ACTIONS, Can, isStandardUser } from "~/routing";
import { generateActionSuccessText, useAxios, useQueryState } from "~/tools";

function Contracts() {
  const [isLoading, setIsLoading] = React.useState(true);

  const state = useParams<RouteParams>();
  const { customerId } = state;
  const { Axios } = useAxios();
  const { showError } = useShowError();
  const [{ limit, page }] = useQueryState();

  const [contracts, setContracts] = React.useState({
    list: [],
    totalCount: "",
  });

  const { role } = useUserConsumer();
  const canEdit = isStandardUser(role);

  const fetchContracts = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(
        `/customers/${customerId}/contracts?limit=${limit}&page=${page}`
      );
      setContracts(() => ({
        list: data.contracts,
        totalCount: data.totalCount,
      }));
    } catch (e) {
      showError(e.response);
    } finally {
      setIsLoading(false);
    }
  }, [Axios, customerId, limit, page, showError]);

  React.useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  const onSubmit = async (values: SubmitContract, type: string) => {
    try {
      switch (type) {
        case Action.CREATE:
          await Axios().post(`/customers/${customerId}/contracts`, {
            ...values,
          });
          showSuccess(generateActionSuccessText(`Contract`)()("posted")());
          break;
        case Action.EDIT:
          await Axios().put(`/customers/${customerId}/contracts/${values.id}`, {
            ...values,
          });
          showSuccess(generateActionSuccessText(`Contract`)()("updated")());
          break;
        case Action.REMOVE:
          await Axios().delete(
            `/customers/${customerId}/contracts/${values.id}`
          );
          showSuccess(generateActionSuccessText(`Contract`)()("deleted")());
          break;
        default:
          return;
      }
    } catch (e) {
      showError(e.response);
    } finally {
      await fetchContracts();
    }
  };

  return (
    <Box>
      <Can action={ACTIONS.ADD_CUSTOMER_CONTRACT}>
        <FlexContainer justify="flex-end" css={{ margin: "0 1rem 0 auto" }}>
          <ContractCreate onSubmit={onSubmit} />
        </FlexContainer>
      </Can>
      <Spacer height="1rem" />

      <LoaderWrap loading={isLoading}>
        {contracts.list.length ? (
          <>
            {contracts.list.map((contract: Contract) => (
              <ContractItem
                key={contract.id}
                contract={contract}
                onSubmit={onSubmit}
                canEdit={canEdit}
              />
            ))}
            <QueryPagination count={Number(contracts?.totalCount) || 0} />
          </>
        ) : (
          <Text>No contracts added.</Text>
        )}
      </LoaderWrap>
    </Box>
  );
}

export { Contracts };
